import { ReactElement, useContext, useState } from 'react';
import * as tsClient from '@tsClient';
import useNoodleApi from '@hooks/useNoodleApi';
import { useToast } from '@hooks';
import TeamsContext from '@providers/Teams/TeamsContext';
import { ToastTypeVariants } from '@context/ToastContext';
import Modal from '@components/Modal';
import TextInput from '@components/FormFields/TextInput';
import PhoneInput from '@components/DesignLibrary/Atoms/PhoneInput';
import Button from '@/components/DesignLibrary/Button';
import Spacer from '@components/Spacer';
import s from './InitiateCaseModal.module.scss';

type CreateCompanyProps = {
  onClose: () => void;
  onCompanyCreated?: (company: { title: string; value: string }) => void;
};

const CreateCompany = ({ onClose, onCompanyCreated }: CreateCompanyProps): ReactElement => {
  const addToast = useToast();
  const { creatorId } = useContext(TeamsContext);

  const [companyName, setCompanyName] = useState('');
  const [companyNameError, setCompanyNameError] = useState<string | null>(null);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);

  const [hasAttemptedToSubmit, setHasAttemptedToSubmit] = useState(false);

  const { getData: createCompanyFn, fetchingState: isCreatingCompany } = useNoodleApi(tsClient.createCompany, {
    toastErrorMessage: error => [ToastTypeVariants.ERROR, error?.errors?.[0] || 'Error creating company. Please try again.'],
    toastOnError: true,
    toastSuccessMessage: () => [ToastTypeVariants.SUCCESS, 'Company created successfully!'],
  });

  const handleSubmit = async (): Promise<void> => {
    if (!creatorId) {
      addToast(ToastTypeVariants.ERROR, 'Unable to determine creator team. Please try refreshing the page or contact support.');
      return;
    }

    setHasAttemptedToSubmit(true);

    if (companyNameError || phoneNumberError) {
      return;
    }

    const response = await createCompanyFn({
      companyName,
      creatorId,
      phoneNumber,
    });

    const company = response.data?.company;

    if (company && onCompanyCreated) {
      onCompanyCreated({
        title: company.name,
        value: company.id,
      });
    }

    onClose();
  };

  return (
    <Modal title="Create new company" onClose={onClose}>
      <div className={s.createCompanyForm}>
        <TextInput
          id="companyName"
          value={companyName}
          onChange={newCompanyName => setCompanyName(newCompanyName)}
          label="Company name"
          placeholder="Company name"
          required
          isTouched={hasAttemptedToSubmit}
          onError={setCompanyNameError}
          error={companyNameError}
        />

        <PhoneInput
          id="phone"
          value={phoneNumber}
          onChange={setPhoneNumber}
          label="Phone"
          isTouched={hasAttemptedToSubmit}
          onError={setPhoneNumberError}
          error={phoneNumberError}
        />

        <Spacer size={8} />

        <Button loading={isCreatingCompany.isFetching} variant="primary" size="md" fullWidth onClick={handleSubmit}>
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default CreateCompany;
